import { useMemo, useState } from 'react';
import { PageHeader } from '../../design-system/primitives';
import { Empty, errorView, Loading } from '../../states/States';
import { useLoad } from '../../views/useLoad';
import { useAppServices } from '../../views/ViewContext';
import { EditorShell } from '../editor/EditorShell';
import type { SchemaNode, SchemaRegistry } from '../editor/schema-properties';
import { ORIGIN_LABELS, viewTitle, ViewsStudioApi, type CustomizableView, type OpenedView } from './views-api';
import { viewsEditorConfig } from './views-config';
import type { View } from './views-model';
import './views.css';

/**
 * Studio › Vues (ADR-130 tranche 2b) : les vues de liste du tenant avec leur couche et leur surcharge, puis
 * l'éditeur d'une vue sur le socle commun du Studio. Une vue du Core ou d'un Pack ne se modifie jamais en
 * place : l'éditer prépare sa surcharge par le client, publiée par `ModelChange`. Proposer et approuver sont
 * des droits distincts, vérifiés au serveur ; masquer une colonne ici n'autorise ni ne refuse rien.
 */

async function loadRegistry(): Promise<SchemaRegistry> {
  const { schemas } = await import('@ayumia/contracts/schemas');
  return Object.fromEntries(Object.values(schemas as Record<string, SchemaNode>).filter((schema) => typeof schema['$id'] === 'string').map((schema) => [schema['$id'] as string, schema]));
}

export function ViewsStudioPage({ artifactId, onOpen, onBack }: { artifactId?: string; onOpen: (artifactId: string) => void; onBack: () => void }) {
  const services = useAppServices();
  const api = useMemo(() => new ViewsStudioApi(services.client), [services.client]);
  return artifactId === undefined ? <ViewList api={api} onOpen={onOpen} /> : <ViewEditor api={api} artifactId={artifactId} onBack={onBack} />;
}

function ViewList({ api, onOpen }: { api: ViewsStudioApi; onOpen: (artifactId: string) => void }) {
  const [loaded, reload] = useLoad('views-studio-list', () => api.list());
  const [search, setSearch] = useState('');
  return (
    <section className="ay-view ay-views" aria-labelledby="ay-views-title">
      <PageHeader titleId="ay-views-title" title="Vues" subtitle="Les listes du tenant, par couche. Une vue du Core ou d’un Pack se personnalise par une surcharge du client — colonnes, filtres, tri, regroupement — jamais en place ; toute publication passe par une proposition." />
      {loaded.status === 'loading' && <Loading label="Lecture des vues…" />}
      {loaded.status === 'error' && errorView(loaded.error, reload)}
      {loaded.status === 'ready' && (
        <>
          <label className="ay-views__search">
            <span className="ay-field__label">Rechercher</span>
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Titre, objet, identifiant" />
          </label>
          <ViewTable items={loaded.value.filter((item) => `${viewTitle(item)} ${item.target_object_type} ${item.artifact_id}`.toLocaleLowerCase('fr').includes(search.trim().toLocaleLowerCase('fr')))} onOpen={onOpen} />
        </>
      )}
    </section>
  );
}

function stateOf(item: CustomizableView): string {
  if (item.overridden_by === undefined) return item.editable_in_place ? 'Vue du client' : 'Telle que livrée';
  return item.parent_moved === true ? `Personnalisée (${item.overridden_by.artifact_version}) — la vue d’origine a changé` : `Personnalisée (${item.overridden_by.artifact_version})`;
}

export function ViewTable({ items, onOpen }: { items: readonly CustomizableView[]; onOpen: (artifactId: string) => void }) {
  if (items.length === 0) return <Empty label="Aucune vue de liste." />;
  return (
    <table className="ay-table ay-views__table">
      <thead>
        <tr>
          <th scope="col">Vue</th>
          <th scope="col">Objet</th>
          <th scope="col">Couche</th>
          <th scope="col">Version</th>
          <th scope="col">État</th>
          <th scope="col"><span className="ay-visually-hidden">Action</span></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.artifact_id}>
            <td>
              <strong>{viewTitle(item)}</strong>
              <div className="ay-text ay-text--muted ay-views__id">{item.artifact_id}</div>
            </td>
            <td>{item.target_object_type}</td>
            <td><span className="ay-badge">{ORIGIN_LABELS[item.origin] ?? item.origin}</span></td>
            <td>{item.artifact_version}</td>
            <td>{stateOf(item)}</td>
            <td>
              <button type="button" className="ay-button ay-button--secondary" onClick={() => onOpen(item.artifact_id)} aria-label={`${item.editable_in_place || item.overridden_by !== undefined ? 'Modifier' : 'Personnaliser'} ${viewTitle(item)}`}>
                {item.editable_in_place || item.overridden_by !== undefined ? 'Modifier' : 'Personnaliser'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ViewEditor({ api, artifactId, onBack }: { api: ViewsStudioApi; artifactId: string; onBack: () => void }) {
  const services = useAppServices();
  const [loaded, reload] = useLoad(`views-studio:${artifactId}`, async () => ({ opened: await api.open(artifactId), registry: await loadRegistry() }));
  return (
    <section className="ay-view ay-views" aria-label="Éditeur de vue">
      <button type="button" className="ay-button ay-button--secondary ay-views__back" onClick={onBack}>← Vues</button>
      {loaded.status === 'loading' && <Loading label="Ouverture de la vue…" />}
      {loaded.status === 'error' && errorView(loaded.error, reload)}
      {loaded.status === 'ready' && <ReadyViewEditor opened={loaded.value.opened} registry={loaded.value.registry} api={api} me={typeof services.context['identity_id'] === 'string' ? services.context['identity_id'] : undefined} onPublished={reload} />}
    </section>
  );
}

export function ReadyViewEditor({ opened, registry, api, me, onPublished }: { opened: OpenedView; registry: SchemaRegistry; api: ViewsStudioApi; me: string | undefined; onPublished: () => void }) {
  const { detail, resolution } = opened;
  const config = useMemo(() => viewsEditorConfig(registry, detail.fields, resolution), [registry, detail.fields, resolution]);
  const port = useMemo(() => api.governance(detail, me), [api, detail, me]);
  // Repartir de la vue d'origine quand elle a changé depuis la copie : la surcharge reprend sa version.
  const [rebased, setRebased] = useState(false);
  const initial: View = rebased && detail.parent_body !== undefined ? ({ ...detail.parent_body, overrides: { view_ref: detail.target.artifact_id, base_version: detail.target.artifact_version } } as View) : detail.body;
  const origin = ORIGIN_LABELS[detail.target.origin] ?? detail.target.origin;
  const layer = detail.edited.override ? `${origin} ${detail.target.artifact_version} › Client${detail.edited.artifact_version === null ? ' (à créer)' : ` ${detail.edited.artifact_version}`}` : `Client ${detail.target.artifact_version}`;
  return (
    <EditorShell<View>
      key={`${detail.edited.artifact_id}@${detail.edited.artifact_version ?? 'new'}:${rebased ? 'rebased' : 'current'}`}
      title={viewTitle({ title: initial.title as Record<string, string> | undefined, artifact_id: detail.target.artifact_id })}
      subtitle={detail.target.artifact_id}
      layer={layer}
      base={detail.body}
      initial={initial}
      config={config}
      port={port}
      onPublished={onPublished}
      aside={
        detail.edited.override ? (
          <div className="ay-views__notice" role="note">
            <p>
              Vue du {origin} : vos modifications forment {detail.edited.exists ? <>une nouvelle version de la surcharge <strong>{detail.edited.artifact_id}</strong></> : <>une <strong>surcharge par le client</strong> (<code>{detail.edited.artifact_id}</code>)</>}, servie à sa place. La vue du {origin} n’est jamais modifiée.
            </p>
            {detail.parent_moved === true && !rebased && (
              <p>
                La vue du {origin} est passée en {detail.target.artifact_version} depuis la copie ({detail.base_version}) : ses changements n’entrent pas d’eux-mêmes.{' '}
                <button type="button" className="ay-button ay-button--secondary" onClick={() => setRebased(true)}>Repartir de la version {detail.target.artifact_version}</button>
              </p>
            )}
          </div>
        ) : undefined
      }
    />
  );
}
